/**
 * Componente para mostrar dashboard de métricas de soporte
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 4.2
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useSoporteData } from '../../hooks/soporte/useSoporteData';
import { ErpContext } from '../../context/ErpContext';
import axios from 'axios';
import Cookies from 'js-cookie';
import { 
  ESTADOS_VISIBLES, 
  LABELS_ESTADOS_VISIBLES,
  MAPEO_ESTADOS
} from '../../utils/soporte/estadosMapping';

const MetricasSoporteComponent = () => {
  const { serverURL } = useContext(ErpContext);
  
  // Obtener rol del usuario
  const userRole = Cookies.get('rolUsuario') || '';
  const esAdmin = userRole === 'Administrador';
  
  // Estado para agente seleccionado (solo para administradores)
  // Por defecto "todos" para mostrar métricas generales
  const [agenteSeleccionado, setAgenteSeleccionado] = useState('todos');
  const [agentes, setAgentes] = useState([]);
  const [cargandoAgentes, setCargandoAgentes] = useState(false);
  
  // Construir parámetros para las métricas
  const paramsMetrics = React.useMemo(() => {
    const params = {};
    if (esAdmin && agenteSeleccionado && agenteSeleccionado !== 'todos') {
      params.agentId = agenteSeleccionado;
    }
    return params;
  }, [esAdmin, agenteSeleccionado]);
  
  const paramsCharts = React.useMemo(() => {
    const params = { period: 30 };
    if (esAdmin && agenteSeleccionado && agenteSeleccionado !== 'todos') {
      params.agentId = agenteSeleccionado;
    }
    return params;
  }, [esAdmin, agenteSeleccionado]);
  
  // Estado para configuración de visualización
  const [configVisualizacion, setConfigVisualizacion] = useState('solo_agentes');
  
  // Cargar configuración de soporte (solo para administradores)
  useEffect(() => {
    if (!esAdmin || !serverURL) return;
    
    const cargarConfig = async () => {
      try {
        const response = await axios.get(`${serverURL}/soporteAPI/config`, {
          withCredentials: true
        });
        const configData = response.data?.config || response.data;
        if (configData) {
          const visualizacion = configData.visualizacionEstadisticas || 'solo_agentes';
          setConfigVisualizacion(visualizacion);
        }
      } catch (error) {
        console.error('Error al cargar configuración:', error);
      }
    };
    
    cargarConfig();
  }, [esAdmin, serverURL]);
  
  // Cargar lista de agentes/administradores (solo para administradores)
  useEffect(() => {
    if (!esAdmin || !serverURL) return;
    
    const cargarUsuarios = async () => {
      setCargandoAgentes(true);
      try {
        const [usuariosRes, rolesRes] = await Promise.all([
          axios.get(`${serverURL}/adminAPI/getUsuario`, { withCredentials: true }),
          axios.get(`${serverURL}/adminAPI/getRolUsuario`, { withCredentials: true })
        ]);
        
        const usuarios = usuariosRes.data?.usuarios || usuariosRes.data || [];
        const roles = rolesRes.data?.roles || rolesRes.data || [];
        
        // Crear mapa de roles
        const rolesMap = {};
        roles.forEach(rol => {
          rolesMap[rol.id] = rol.descripcion || rol.nombre;
        });
        
        // Filtrar usuarios según la configuración
        let usuariosFiltrados = [];
        
        if (configVisualizacion === 'solo_agentes') {
          // Solo agentes (Agente Soporte o Soporte)
          usuariosFiltrados = usuarios.filter(usuario => {
            const rolDescripcion = rolesMap[usuario.rol] || '';
            return rolDescripcion === 'Agente Soporte' || rolDescripcion === 'Soporte';
          });
        } else if (configVisualizacion === 'administradores_y_agentes') {
          // Agentes y administradores
          usuariosFiltrados = usuarios.filter(usuario => {
            const rolDescripcion = rolesMap[usuario.rol] || '';
            return rolDescripcion === 'Agente Soporte' || 
                   rolDescripcion === 'Soporte' || 
                   rolDescripcion === 'Administrador';
          });
        }
        
        setAgentes(usuariosFiltrados);
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
      } finally {
        setCargandoAgentes(false);
      }
    };
    
    cargarUsuarios();
  }, [esAdmin, serverURL, configVisualizacion]);
  
  // Cargar métricas de cards
  const { data: metricsData, loading: metricsLoading, reload: reloadMetrics } = 
    useSoporteData('/metrics/cards', paramsMetrics);
  
  // Cargar datos de gráficas
  const { data: chartsData, loading: chartsLoading, reload: reloadCharts } = 
    useSoporteData('/metrics/charts', paramsCharts);
  
  // Cargar promedio de respuesta
  const { data: promedioData, loading: promedioLoading, reload: reloadPromedio } = 
    useSoporteData('/metrics/promedio-respuesta', paramsMetrics);
  
  const metrics = metricsData?.metrics || metricsData || {};
  const charts = chartsData?.charts || chartsData || {};
  const promedioDias = promedioData?.promedioDias || 0;
  
  /**
   * Función para actualizar todas las métricas
   */
  const handleRefresh = () => {
    reloadMetrics();
    reloadCharts();
    reloadPromedio();
  };
  
  /**
   * Manejar cambio de agente seleccionado
   */
  const handleAgenteChange = (event) => {
    setAgenteSeleccionado(event.target.value);
  };
  
  // Preparar datos para gráficas
  const pieEstadoData = React.useMemo(() => {
    const porEstado = metrics.porEstado || {};
    
    // Agrupar estados internos según el mapeo a estados visibles
    const estadosAgrupados = {
      [ESTADOS_VISIBLES.ABIERTO]: 0,
      [ESTADOS_VISIBLES.EN_PROGRESO]: 0,
      [ESTADOS_VISIBLES.COMPLETADO]: 0,
      [ESTADOS_VISIBLES.PRUEBA]: 0,
      [ESTADOS_VISIBLES.CERRADO]: 0
    };
    
    // Sumar los valores de los estados internos que corresponden a cada estado visible
    Object.keys(MAPEO_ESTADOS).forEach(estadoInterno => {
      const estadoVisible = MAPEO_ESTADOS[estadoInterno];
      const valor = porEstado[estadoInterno] || 0;
      if (estadosAgrupados[estadoVisible] !== undefined) {
        estadosAgrupados[estadoVisible] += valor;
      }
    });
    
    // Convertir a formato para el gráfico
    return [
      { 
        id: 0, 
        value: estadosAgrupados[ESTADOS_VISIBLES.ABIERTO], 
        label: LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.ABIERTO] 
      },
      { 
        id: 1, 
        value: estadosAgrupados[ESTADOS_VISIBLES.EN_PROGRESO], 
        label: LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.EN_PROGRESO] 
      },
      { 
        id: 2, 
        value: estadosAgrupados[ESTADOS_VISIBLES.COMPLETADO], 
        label: LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.COMPLETADO] 
      },
      { 
        id: 3, 
        value: estadosAgrupados[ESTADOS_VISIBLES.PRUEBA], 
        label: LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.PRUEBA] 
      },
      { 
        id: 4, 
        value: estadosAgrupados[ESTADOS_VISIBLES.CERRADO], 
        label: LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.CERRADO] 
      }
    ].filter(item => item.value > 0); // Solo mostrar estados con tickets
  }, [metrics.porEstado]);
  
  const piePrioridadData = React.useMemo(() => {
    // El backend devuelve distribucionPorPrioridad con labels y data
    // Necesitamos transformarlo al formato esperado por PieChart
    if (charts.distribucionPorPrioridad) {
      const { labels, data } = charts.distribucionPorPrioridad;
      return labels.map((label, index) => ({
        id: index,
        value: data[index] || 0,
        label: label
      })).filter(item => item.value > 0);
    }
    return [];
  }, [charts.distribucionPorPrioridad]);

  const pieUsuarioData = React.useMemo(() => {
    // Gráfica de asignación por usuario (tickets NO CERRADOS)
    if (charts.distribucionPorUsuario) {
      const { labels, data, rawData } = charts.distribucionPorUsuario;
      
      // Si hay un agente seleccionado (no "todos"), mostrar solo ese agente
      if (esAdmin && agenteSeleccionado && agenteSeleccionado !== 'todos') {
        const agenteId = parseInt(agenteSeleccionado);
        // Buscar el agente en rawData
        const agente = agentes.find(a => a.id === agenteId);
        
        if (agente) {
          const nombreAgente = agente.nombre && agente.apellido 
            ? `${agente.nombre} ${agente.apellido}` 
            : agente.usuario || `Usuario ${agente.id}`;
          
          // Encontrar los datos de este agente
          const agenteData = rawData.find(item => {
            const itemId = item.agentid || item.agentId;
            return itemId === agenteId;
          });
          
          const count = agenteData ? parseInt(agenteData.count) : 0;
          
          return [{
            id: 0,
            value: count,
            label: `${nombreAgente} (${count})`
          }];
        }
        return [];
      }
      
      // Mostrar distribución general
      // Asegurarnos de que "Sin asignar" sea único
      const datosUnicos = [];
      const labelsVistos = new Set();
      
      labels.forEach((label, index) => {
        if (!labelsVistos.has(label) && data[index] > 0) {
          labelsVistos.add(label);
          datosUnicos.push({
            id: index,
            value: data[index],
            label: label
          });
        }
      });
      
      return datosUnicos;
    }
    return [];
  }, [charts.distribucionPorUsuario, esAdmin, agenteSeleccionado, agentes]);
  
  const barCategoriaData = React.useMemo(() => {
    // El backend devuelve porCategoria como objeto
    const porCategoria = metrics.porCategoria || {};
    const categorias = Object.keys(porCategoria);
    return {
      labels: categorias,
      data: categorias.map(cat => porCategoria[cat] || 0)
    };
  }, [metrics.porCategoria]);
  
  const lineTendenciaData = React.useMemo(() => {
    // Combinar tendenciaCreados y tendenciaResueltos
    const creados = charts.tendenciaCreados || [];
    const resueltos = charts.tendenciaResueltos || [];
    
    // Obtener todas las fechas únicas
    const fechas = new Set();
    creados.forEach(item => fechas.add(item.fecha));
    resueltos.forEach(item => fechas.add(item.fecha));
    const fechasOrdenadas = Array.from(fechas).sort();
    
    // Crear mapa para acceso rápido
    const creadosMap = new Map(creados.map(item => [item.fecha, item.count]));
    const resueltosMap = new Map(resueltos.map(item => [item.fecha, item.count]));
    
    return {
      fechas: fechasOrdenadas,
      creados: fechasOrdenadas.map(fecha => creadosMap.get(fecha) || 0),
      resueltos: fechasOrdenadas.map(fecha => resueltosMap.get(fecha) || 0)
    };
  }, [charts.tendenciaCreados, charts.tendenciaResueltos]);
  
  const loading = metricsLoading || chartsLoading || promedioLoading;
  const error = metricsData?.error || chartsData?.error || promedioData?.error;
  
  if (loading && !metrics && !charts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <IconButton onClick={handleRefresh} sx={{ mt: 2 }}>
          <RefreshIcon />
        </IconButton>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 ,caretColor: 'transparent'}}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Métricas de Soporte</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Selector de agente (solo para administradores) */}
          {esAdmin && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filtrar por Agente</InputLabel>
              <Select
                value={agenteSeleccionado}
                onChange={handleAgenteChange}
                label="Filtrar por Agente"
                disabled={cargandoAgentes}
              >
                <MenuItem value="todos">Todos los Tickets</MenuItem>
                {agentes.map(agente => (
                  <MenuItem key={agente.id} value={agente.id}>
                    {agente.nombre && agente.apellido 
                      ? `${agente.nombre} ${agente.apellido}` 
                      : agente.usuario || `Usuario ${agente.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Actualizar">
            <span>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
      
      {/* Cards de Métricas Principales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card elevation={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h3" color="primary.main">
                {metrics.total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tickets Activos
              </Typography>
              {metrics.totalAyer !== undefined && (
                <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                  {metrics.total > metrics.totalAyer ? (
                    <TrendingUpIcon fontSize="small" color="success" />
                  ) : (
                    <TrendingDownIcon fontSize="small" color="error" />
                  )}
                  <Typography variant="caption">
                    vs ayer: {metrics.totalAyer}
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card elevation={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h3" color="info.main">
                {promedioDias.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tiempo Promedio Resolución
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                días (creación → resolución)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card elevation={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h3" color="error.main">
                -{/* {metrics.slaViolado || 0} */}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                SLA Vencido
              </Typography>
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                Prioridad crítica
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card elevation={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h3" color="success.main">
                {metrics.resueltosHoy || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Resueltos Hoy
              </Typography>
              {metrics.metaDiaria && (
                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                  Meta: {metrics.metaDiaria}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Gráficas */}
      <Grid container spacing={3}>
          {/* Gráfica de Pie - Estado */}
          <Grid item xs={12} lg={4}>
           <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
             <Typography variant="h6" gutterBottom>
               Tickets por Estado
             </Typography>
             {pieEstadoData.length > 0 ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                 <PieChart
                   series={[
                     {
                       data: pieEstadoData,
                       highlightScope: { faded: 'global', highlighted: 'item' },
                       faded: { innerRadius: 30, additionalRadius: -30 }
                     }
                   ]}
                   width={480}
                   height={300}
                   slotProps={{
                     legend: {
                       direction: 'column',
                       position: { vertical: 'middle', horizontal: 'right' },
                       padding: { left: 50, right: 0, top: 0, bottom: 0 },
                       itemMarkWidth: 20,
                       itemMarkHeight: 20,
                       markGap: 5,
                       itemGap: 10
                     }
                   }}
                 />
               </Box>
             ) : (
               <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                 <Typography variant="body2" color="text.secondary">
                   No hay datos disponibles
                 </Typography>
               </Box>
             )}
           </Paper>
         </Grid>
        
         {/* Gráfica de Pie - Prioridad */}
         <Grid item xs={12} lg={4}>
           <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
             <Typography variant="h6" gutterBottom>
               Tickets por Prioridad
             </Typography>
             {piePrioridadData.length > 0 ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                 <PieChart
                   series={[
                     {
                       data: piePrioridadData,
                       highlightScope: { faded: 'global', highlighted: 'item' },
                       faded: { innerRadius: 30, additionalRadius: -30 }
                     }
                   ]}
                   width={480}
                   height={300}
                   slotProps={{
                     legend: {
                       direction: 'column',
                       position: { vertical: 'middle', horizontal: 'right' },
                       padding: { left: 50, right: 0, top: 0, bottom: 0 },
                       itemMarkWidth: 20,
                       itemMarkHeight: 20,
                       markGap: 5,
                       itemGap: 10
                     }
                   }}
                 />
               </Box>
             ) : (
               <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                 <Typography variant="body2" color="text.secondary">
                   No hay datos disponibles
                 </Typography>
               </Box>
             )}
           </Paper>
         </Grid>
        
         {/* Gráfica de Pie - Asignación por Usuario */}
         <Grid item xs={12} lg={4}>
           <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
             <Typography variant="h6" gutterBottom>
               {esAdmin && agenteSeleccionado && agenteSeleccionado !== 'todos' 
                 ? 'Tickets Asignados' 
                 : 'Tickets por Agente'}
             </Typography>
             {pieUsuarioData.length > 0 ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                 <PieChart
                   series={[
                     {
                       data: pieUsuarioData,
                       highlightScope: { faded: 'global', highlighted: 'item' },
                       faded: { innerRadius: 30, additionalRadius: -30 }
                     }
                   ]}
                   width={600}
                   height={300}
                   slotProps={{
                     legend: {
                       direction: 'column',
                       position: { vertical: 'middle', horizontal: 'right' },
                       padding: { left: 100, right: 0, top: 0, bottom: 0 },
                       itemMarkWidth: 20,
                       itemMarkHeight: 20,
                       markGap: 5,
                       itemGap: 10
                     }
                   }}
                 />
               </Box>
             ) : (
               <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                 <Typography variant="body2" color="text.secondary">
                   No hay datos disponibles
                 </Typography>
               </Box>
             )}
           </Paper>
         </Grid>
         
         {/* Gráfica de Barras - Categorías */}
         <Grid item xs={12} lg={6}>
           <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
             <Typography variant="h6" gutterBottom>
               Tickets por Categoría
             </Typography>
             {barCategoriaData.labels && barCategoriaData.labels.length > 0 ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                 <BarChart
                   xAxis={[
                     {
                       scaleType: 'band',
                       data: barCategoriaData.labels
                     }
                   ]}
                   series={[
                     {
                       data: barCategoriaData.data,
                       label: 'Tickets'
                     }
                   ]}
                   width={480}
                   height={300}
                 />
               </Box>
             ) : (
               <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                 <Typography variant="body2" color="text.secondary">
                   No hay datos disponibles
                 </Typography>
               </Box>
             )}
           </Paper>
         </Grid>
         
         {/* Gráfica de Línea - Tendencia 30 días */}
         <Grid item xs={12} lg={6}>
           <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
             <Typography variant="h6" gutterBottom>
               Tendencia Últimos 30 Días
             </Typography>
             {lineTendenciaData.fechas && lineTendenciaData.fechas.length > 0 ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                 <LineChart
                   xAxis={[
                     {
                       scaleType: 'band',
                       data: lineTendenciaData.fechas.map(fecha => {
                         // Formatear fecha para mostrar solo día/mes
                         const date = new Date(fecha);
                         return `${date.getDate()}/${date.getMonth() + 1}`;
                       })
                     }
                   ]}
                   series={[
                     {
                       data: lineTendenciaData.creados,
                       label: 'Creados',
                       color: '#1976d2'
                     },
                     {
                       data: lineTendenciaData.resueltos,
                       label: 'Resueltos',
                       color: '#2e7d32'
                     }
                   ]}
                   width={480}
                   height={300}
                 />
               </Box>
             ) : (
               <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                 <Typography variant="body2" color="text.secondary">
                   No hay datos disponibles
                 </Typography>
               </Box>
             )}
           </Paper>
         </Grid>
      </Grid>
    </Box>
  );
};

export default MetricasSoporteComponent;
